// utils/courseUtils.ts

import type { WorkloadThresholds } from '../contexts/InstitutionConfigContext';

interface CourseWithCredits {
  credits?: number | string;
  grade_data?: {
    average_gpa?: number | null;
    difficulty?: string | null;
  } | null;
}

type DifficultyLevel = 'easy' | 'moderate' | 'challenging' | 'hard';

interface DifficultyCounts {
  easy: number;
  moderate: number;
  challenging: number;
  hard: number;
}

// Check if a semester is summer or winter (special semester) - UPDATED for numeric format
export const isSpecialSemester = (semester: string): boolean => {
  // Handle AP credits and Other credits
  if (semester === '000008' || semester === '000009') {
    return false; // Prior learning credits are not special semesters
  }

  // Handle numeric format (YYYYMM)
  if (semester && semester.length === 6 && /^\d{6}$/.test(semester)) {
    const month = semester.substring(4, 6);
    return month === '05' || month === '12'; // Summer or Winter
  }

  // Handle readable format (backwards compatibility)
  if (semester && semester.includes('-')) {
    const type = semester.split('-')[0];
    return type === 'winter' || type === 'summer';
  }

  return false;
};


// Calculate total credits for a list of courses
export const calculateTotalCredits = (courses: CourseWithCredits[]): number => {
  return courses.reduce((sum: number, course: CourseWithCredits) => {
    // NaN (missing/unparseable) falls back to 3; a legitimate 0 stays 0 —
    // `|| 3` treated zero-credit entries as 3 (round-3 review finding; the
    // backend explicitly permits credits: 0 on placeholders).
    const parsed = parseFloat(String(course.credits));
    return sum + (Number.isNaN(parsed) ? 3 : parsed);
  }, 0);
};

/**
 * Calculate the average GPA for a list of courses based on grade_data
 * @param {Array} courses - Array of course objects with optional grade_data
 * @returns {number|null} - Average GPA or null if no courses have grade data
 */
const calculateAverageGPA = (courses: CourseWithCredits[]): number | null => {
  if (!courses || !courses.length) return null;

  const coursesWithGrades = courses.filter(c => c.grade_data?.average_gpa != null);
  if (coursesWithGrades.length === 0) return null;

  const sum = coursesWithGrades.reduce((acc, c) => acc + c.grade_data!.average_gpa!, 0);
  return sum / coursesWithGrades.length;
};

/**
 * Count courses by difficulty level
 * @param {Array} courses - Array of course objects with optional grade_data
 * @returns {Object} - Counts by difficulty: { easy, moderate, challenging, hard }
 */
const countByDifficulty = (courses: CourseWithCredits[]): DifficultyCounts => {
  if (!courses || !courses.length) {
    return { easy: 0, moderate: 0, challenging: 0, hard: 0 };
  }

  return courses.reduce((counts: DifficultyCounts, course: CourseWithCredits) => {
    const difficulty = course.grade_data?.difficulty as DifficultyLevel | undefined;
    if (difficulty && difficulty in counts) {
      counts[difficulty]++;
    }
    return counts;
  }, { easy: 0, moderate: 0, challenging: 0, hard: 0 });
};

/** UMD's normal load and per-semester maximum: the scale the warning used
 *  before it read institution config, and the fallback when none is given.
 *  The one frontend copy: InstitutionConfigContext's FALLBACK_CONFIG reuses it
 *  (the backend's lives in institution_config_service.default_workload_thresholds). */
export const DEFAULT_WORKLOAD_THRESHOLDS: WorkloadThresholds = { moderateAbove: 16, heavyAbove: 18 };

/**
 * Analyze semester workload based on credits, course difficulty, and average GPA
 * @param {Array} courses - Array of course objects
 * @returns {Object} - { level: 'light'|'moderate'|'heavy', message: string, details: Object }
 */
export const analyzeSemesterWorkload = (
  courses: CourseWithCredits[] | null | undefined,
  thresholds: WorkloadThresholds = DEFAULT_WORKLOAD_THRESHOLDS,
) => {
  if (!courses || !courses.length) {
    return {
      level: 'light',
      message: '',
      details: {
        totalCredits: 0,
        averageGpa: null,
        difficulties: { easy: 0, moderate: 0, challenging: 0, hard: 0 },
        courseCount: 0
      }
    };
  }

  const totalCredits = calculateTotalCredits(courses);
  const avgGpa = calculateAverageGPA(courses);
  const difficulties = countByDifficulty(courses);
  const hardAndChallenging = difficulties.hard + difficulties.challenging;

  let level = 'light';
  const messages = [];

  // Credit load, against the institution's own scale: heavy above its
  // per-semester maximum, moderate above its normal load. A fixed 16/18 read
  // every normal 30-ECTS semester at UNIC as "a very heavy load".
  if (totalCredits > thresholds.heavyAbove) {
    level = 'heavy';
    messages.push(`${totalCredits} credits is a very heavy load`);
  } else if (totalCredits > thresholds.moderateAbove) {
    level = level === 'light' ? 'moderate' : level;
    messages.push(`${totalCredits} credits may require advisor approval`);
  }

  // Multiple hard/challenging courses
  if (difficulties.hard >= 3) {
    level = 'heavy';
    messages.push(`${difficulties.hard} courses with historically low GPAs`);
  } else if (difficulties.hard >= 2 || hardAndChallenging >= 4) {
    level = level === 'light' ? 'moderate' : level;
    if (difficulties.hard >= 2) {
      messages.push(`${difficulties.hard} difficult courses`);
    } else {
      messages.push(`${hardAndChallenging} challenging/hard courses`);
    }
  }

  // Low average GPA for semester
  if (avgGpa !== null) {
    if (avgGpa < 2.5) {
      level = 'heavy';
      messages.push(`Average course GPA is ${avgGpa.toFixed(2)}`);
    } else if (avgGpa < 2.8) {
      level = level === 'light' ? 'moderate' : level;
      messages.push(`Average course GPA is ${avgGpa.toFixed(2)}`);
    }
  }

  return {
    level,
    message: messages.join('; '),
    details: {
      totalCredits,
      averageGpa: avgGpa,
      difficulties,
      courseCount: courses.length
    }
  };
};
